import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Stack,
  Paper,
  Typography,
  Box,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  PowerSettingsNew as PowerIcon,
  PowerOff as PowerOffIcon,
} from '@mui/icons-material';

const DS = {
  colors: {
    background: { primary: '#0f1419', secondary: '#1a1f2e' },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
      tertiary: 'rgba(255, 255, 255, 0.5)',
      muted: 'rgba(255, 255, 255, 0.3)',
    },
    status: { success: '#00ff88', error: '#ff4757', warning: '#ffa500', info: '#00d4ff' },
    accent: { primary: '#00d4ff', secondary: '#764ba2', tertiary: '#00ff88' },
  },
};

const StrategyTable = ({
  strategies,
  loading,
  onEdit,
  onView,
  onDelete,
  onRefresh,
  onToggleActive,
  submitting,
  showInactive = false,
}) => {
  // Filter strategies based on showInactive prop
  const displayStrategies = showInactive 
    ? strategies 
    : strategies.filter(s => s.is_active !== false);

  return (
    <TableContainer
      component={Paper}
      sx={{
        bgcolor: 'rgba(26, 31, 46, 0.6)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: 1.5,
        backdropFilter: 'blur(8px)',
        overflow: 'hidden',
      }}
    >
      {displayStrategies.length === 0 && !loading && (
        <Box
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: 'rgba(26, 31, 46, 0.3)',
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.4)',
              fontWeight: 500,
            }}
          >
            {showInactive && strategies.length > 0 
              ? 'All strategies are active. No inactive strategies found.'
              : 'No strategies found. Create one to get started.'}
          </Typography>
        </Box>
      )}

      <Table sx={{ minWidth: 1000 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'rgba(0, 212, 255, 0.04)' }}>
            <TableCell sx={{ color: DS.colors.accent.primary, fontWeight: 700, fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', py: 2, px: 2.5 }}>
              Strategy
            </TableCell>

            <TableCell sx={{ color: DS.colors.accent.primary, fontWeight: 700, fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', py: 2, px: 2.5 }}>
              Symbol
            </TableCell>

            <TableCell sx={{ color: DS.colors.accent.primary, fontWeight: 700, fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', py: 2, px: 2.5 }}>
              Exit Signal
            </TableCell>

            <TableCell sx={{ color: DS.colors.accent.primary, fontWeight: 700, fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', py: 2, px: 2.5 }}>
              Conditions
            </TableCell>

            <TableCell sx={{ color: DS.colors.accent.primary, fontWeight: 700, fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', py: 2, px: 2.5 }}>
              Progress
            </TableCell>

            <TableCell sx={{ color: DS.colors.accent.primary, fontWeight: 700, fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', py: 2, px: 2.5 }}>
              Status
            </TableCell>

            <TableCell sx={{ color: DS.colors.accent.primary, fontWeight: 700, fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', py: 2, px: 2.5, textAlign: 'right' }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {displayStrategies.map((strategy) => (
            <TableRow
              key={strategy.bulk_strategy_id}
              sx={{
                bgcolor: strategy.current_state?.in_position
                  ? 'rgba(0, 255, 136, 0.03)'
                  : 'transparent',
                borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                opacity: strategy.is_active === false ? 0.5 : 1,
                '&:hover': {
                  bgcolor: strategy.is_active === false 
                    ? 'rgba(255, 71, 87, 0.03)' 
                    : 'rgba(0, 212, 255, 0.05)',
                },
              }}
            >
              <TableCell sx={{ color: DS.colors.accent.primary, fontWeight: 600, fontSize: '13px', py: 1.75, px: 2.5 }}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                      {strategy.bulk_strategy_id}
                    </Typography>
                    {strategy.is_active === false && (
                      <Chip
                        label="INACTIVE"
                        size="small"
                        sx={{
                          height: '18px',
                          fontSize: '10px',
                          fontWeight: 700,
                          bgcolor: 'rgba(255, 71, 87, 0.2)',
                          color: DS.colors.status.error,
                          border: '1px solid rgba(255, 71, 87, 0.3)',
                        }}
                      />
                    )}
                  </Stack>
                  <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.4)', mt: 0.25 }}>
                    {strategy.description || 'No description'}
                  </Typography>
                </Box>
              </TableCell>

              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px', py: 1.75, px: 2.5 }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Chip
                    label={strategy.symbol}
                    size="small"
                    sx={{
                      bgcolor: strategy.is_active === false 
                        ? 'rgba(255, 255, 255, 0.04)' 
                        : 'rgba(255, 255, 255, 0.08)',
                      color: strategy.is_active === false 
                        ? 'rgba(255, 255, 255, 0.5)' 
                        : '#fff',
                      fontWeight: 600,
                      fontSize: '11px',
                    }}
                  />
                  <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)' }}>
                    {strategy.timeframe}
                  </Typography>
                </Stack>
              </TableCell>

              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', py: 1.75, px: 2.5 }}>
                <Chip
                  label={strategy.configuration?.exit_signal || 'N/A'}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: strategy.is_active === false 
                      ? 'rgba(255, 71, 87, 0.1)' 
                      : 'rgba(255, 71, 87, 0.3)',
                    color: strategy.is_active === false 
                      ? 'rgba(255, 71, 87, 0.5)' 
                      : DS.colors.status.error,
                    fontSize: '11px',
                    fontWeight: 500,
                  }}
                />
              </TableCell>

              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', py: 1.75, px: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: strategy.is_active === false 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 212, 255, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: strategy.is_active === false 
                        ? 'rgba(255, 255, 255, 0.3)' 
                        : DS.colors.accent.primary,
                      fontWeight: 700,
                      fontSize: '11px',
                    }}
                  >
                    {strategy.configuration?.sub_strategies_count || strategy.sub_strategies?.length || 0}
                  </Box>
                </Box>
              </TableCell>

              <TableCell sx={{ py: 1.75, px: 2.5 }}>
                <Chip
                  label={`${strategy.progress?.completed || 0}/${strategy.progress?.total || 0}`}
                  size="small"
                  sx={{
                    bgcolor: alpha(
                      strategy.progress?.percentage === 100 
                        ? DS.colors.status.success 
                        : strategy.is_active === false 
                          ? 'rgba(255, 255, 255, 0.05)'
                          : DS.colors.status.warning,
                      0.15
                    ),
                    color: strategy.progress?.percentage === 100 
                      ? DS.colors.status.success 
                      : strategy.is_active === false 
                        ? 'rgba(255, 255, 255, 0.3)'
                        : DS.colors.status.warning,
                    fontWeight: 600,
                    fontSize: '11px',
                  }}
                />
              </TableCell>

              <TableCell sx={{ py: 1.75, px: 2.5 }}>
                <Stack direction="row" spacing={0.5}>
                  {strategy.is_active === false ? (
                    <Chip
                      label="DEACTIVATED"
                      size="small"
                      icon={<PowerOffIcon />}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        color: 'rgba(255, 255, 255, 0.4)',
                        fontWeight: 600,
                        fontSize: '11px',
                        '& .MuiChip-icon': {
                          fontSize: '14px',
                          color: 'rgba(255, 255, 255, 0.3)',
                        },
                      }}
                    />
                  ) : (
                    <Chip
                      label={strategy.current_state?.in_position ? 'IN POSITION' : 'IDLE'}
                      size="small"
                      icon={strategy.current_state?.in_position ? <PlayIcon /> : <StopIcon />}
                      sx={{
                        bgcolor: alpha(
                          strategy.current_state?.in_position ? DS.colors.status.success : 'rgba(255, 255, 255, 0.1)',
                          0.2
                        ),
                        color: strategy.current_state?.in_position ? DS.colors.status.success : 'rgba(255, 255, 255, 0.5)',
                        fontWeight: 600,
                        fontSize: '11px',
                        '& .MuiChip-icon': {
                          fontSize: '14px',
                        },
                      }}
                    />
                  )}
                </Stack>
              </TableCell>

              <TableCell sx={{ py: 1.75, px: 2.5, textAlign: 'right' }}>
                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => onView(strategy)}
                      sx={{
                        color: '#0ea5e9',
                        transition: 'all 150ms',
                        '&:hover': { bgcolor: 'rgba(14, 165, 233, 0.1)' },
                      }}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Refresh Strategy">
                    <IconButton
                      size="small"
                      onClick={() => onRefresh(strategy)}
                      disabled={submitting || strategy.is_active === false}
                      sx={{
                        color: DS.colors.accent.secondary,
                        transition: 'all 150ms',
                        '&:hover': { bgcolor: alpha(DS.colors.accent.secondary, 0.1) },
                        '&:disabled': { opacity: 0.3, cursor: 'not-allowed' },
                      }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={strategy.is_active === false ? 'Activate Strategy' : 'Deactivate Strategy'}>
                    <IconButton
                      size="small"
                      onClick={() => onToggleActive(strategy)}
                      disabled={submitting || (strategy.is_active !== false && strategy.current_state?.in_position)}
                      sx={{
                        color: strategy.is_active === false 
                          ? DS.colors.status.success 
                          : DS.colors.status.warning,
                        transition: 'all 150ms',
                        '&:hover': { 
                          bgcolor: alpha(
                            strategy.is_active === false 
                              ? DS.colors.status.success 
                              : DS.colors.status.warning, 
                            0.1
                          ) 
                        },
                        '&:disabled': { opacity: 0.3, cursor: 'not-allowed' },
                      }}
                    >
                      {strategy.is_active === false ? <PowerIcon fontSize="small" /> : <PowerOffIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(strategy)}
                      disabled={strategy.is_active === false}
                      sx={{
                        color: DS.colors.status.warning,
                        transition: 'all 150ms',
                        '&:hover': { bgcolor: 'rgba(255, 165, 0, 0.1)' },
                        '&:disabled': { opacity: 0.3, cursor: 'not-allowed' },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={strategy.current_state?.in_position ? 'Cannot Delete (In Position)' : 'Delete Strategy'}>
                    <IconButton
                      size="small"
                      onClick={() => onDelete(strategy)}
                      disabled={submitting || strategy.current_state?.in_position}
                      sx={{
                        color: DS.colors.status.error,
                        transition: 'all 150ms',
                        '&:hover': { bgcolor: 'rgba(255, 71, 87, 0.1)' },
                        '&:disabled': { opacity: 0.3, cursor: 'not-allowed' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default StrategyTable;
